import React, {
  MouseEventHandler, ReactElement, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import Slider, { Settings } from 'react-slick';
import { AdvancedImage, AdvancedVideo, lazyload } from '@cloudinary/react';
import { Cloudinary, CloudinaryImage, CloudinaryVideo } from '@cloudinary/url-gen';
import { thumbnail } from '@cloudinary/url-gen/actions/resize';
import { byRadius } from '@cloudinary/url-gen/actions/roundCorners';
import { name } from '@cloudinary/url-gen/actions/namedTransformation';
import { set } from '@cloudinary/url-gen/actions/variable';
import { CLOUDINARY_CLOUD_NAME, PUPPY_DEFAULT_IMAGE } from '../../../utils/constants';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { setFallbackImage } from '../../../utils/imageHelper';
import BoostBadge from './components/boost-badge';

interface ListingSliderImage {
  publicId: string;
  mediaType: string;
  fileType: string;
  overlayDate: string;
  parentLabel: string;
}

interface ListingSliderProps {
  images: ListingSliderImage[];
  externalMedia: any;
  listingName: string | null;
  listingType: string | null;
  listingBreedName: string | null;
  listingBoosted: boolean;
}

interface ArrowProps {
  label: string;
  className?: string;
  style?: any;
  onClick?: MouseEventHandler;
}

const ListingSlider: React.FC<ListingSliderProps> = (data): ReactElement => {
  const {
    images,
    externalMedia,
    listingName,
    listingType,
    listingBreedName,
    listingBoosted,
  } = data;

  const cloudinary = new Cloudinary({
    cloud: {
      cloudName: CLOUDINARY_CLOUD_NAME,
    },
  });
  const mediaCount = images.length + externalMedia.length;
  const videoCount = images.filter((item) => item.mediaType === 'video').length + externalMedia.length;
  const initialSlide = 0;

  const [activeSlide, setActiveSlide] = useState(0);
  const [slider1, setSlider1] = useState<Slider | undefined>(undefined);
  const [slider2, setSlider2] = useState<Slider | undefined>(undefined);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(window.innerWidth >= 993);

  const listingAltTitleAttr = () => {
    if (!listingName || !listingType || !listingBreedName) {
      return '';
    }
    const listingTypeTitleCase = listingType === 'puppy' ? 'Puppy' : 'Stud';
    return `${listingBreedName} ${listingTypeTitleCase} named ${listingName}`;
  };

  const handleSlideChange = (current: number, next: number) => {
    if (current !== 0 && next !== initialSlide && !hasScrolled) {
      setHasScrolled(true);
    }
    setActiveSlide(next);
  };

  useEffect(() => {
    if (slider2) {
      slider2.slickGoTo(initialSlide);
    }
  }, [slider2]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 993) {
        setShowThumbnails(false);
      } else {
        setShowThumbnails(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const PrevNextArrow = ({
    label,
    className,
    style,
    onClick,
  }: ArrowProps) => {
    return (
      <button
        className={className}
        style={style}
        onClick={onClick}
        aria-disabled={onClick ? 'false' : 'true'}
        aria-label={label}
      />
    );
  };

  const settings: Settings = {
    className: 'listing-slider-main',
    infinite: false,
    centerPadding: '60px',
    slidesToShow: 1,
    arrows: mediaCount > 1,
    prevArrow: <PrevNextArrow label="Previous Slide" />,
    nextArrow: <PrevNextArrow label="Next Slide" />,
  };

  const settingsTabs: Settings = {
    centerMode: false,
    infinite: false,
    vertical: true,
    arrows: mediaCount > 8,
    slidesToShow: 8,
    slidesToScroll: 8,
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          arrows: mediaCount > 6,
          slidesToShow: 6,
          slidesToScroll: 6,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
          vertical: false,
          arrows: false,
        },
      },
    ],
  };

  const cloudMedia = images.map((media) => {
    const sliderMedia = {
      publicId: media.publicId,
      mediaType: media.mediaType,
      thumb: CloudinaryImage || CloudinaryVideo,
      embedUrl: CloudinaryImage || CloudinaryVideo,
    };

    if (media.mediaType === 'video') {
      // @ts-ignore
      sliderMedia.thumb = cloudinary.video(media.publicId)
        .resize(thumbnail()
          .width(115)
          .height(115))
        .roundCorners(byRadius(8))
        .format('jpg'); // Converts frame of video to an image

      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.video(media.publicId)
        .roundCorners(byRadius(16));

      return sliderMedia;
    }

    if (media.parentLabel) {
      // @ts-ignore
      sliderMedia.thumb = cloudinary
        .image(media.publicId)
        .addVariable(set('parentLabel', media.parentLabel))
        .resize(thumbnail()
          .width(150)
          .height(150))
        .namedTransformation(name('lpr-parent-transform'))
        .roundCorners(byRadius(8));
    } else {
      // @ts-ignore
      sliderMedia.thumb = cloudinary
        .image(media.publicId)
        .resize(thumbnail()
          .width(150)
          .height(150))
        .roundCorners(byRadius(8));
    }

    if (media.parentLabel) {
      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.image(media.publicId)
        .addVariable(set('date', media.overlayDate))
        .addVariable(set('parentLabel', media.parentLabel))
        .namedTransformation(name('lpr-full-transform'))
        .namedTransformation(name('lpr-date-transform'))
        .namedTransformation(name('lpr-parent-transform'))
        .roundCorners(byRadius(16));
    } else {
      // @ts-ignore
      sliderMedia.embedUrl = cloudinary.image(media.publicId)
        .addVariable(set('date', media.overlayDate))
        .namedTransformation(name('lpr-full-transform'))
        .namedTransformation(name('lpr-date-transform'))
        .roundCorners(byRadius(16));
    }

    return sliderMedia;
  });

  const allMedia = [
    ...cloudMedia,
    ...externalMedia,
  ];

  return (
    <>
      <div className="d-flex flex-lg-row flex-wrap-reverse">
        {showThumbnails && (
          <div className="slider-container-1 col-12 col-lg-auto">
            <div
              className="slider-wrap"
            >

              {/* THUMBNAILS SLIDER */}
              <Slider
                {...settingsTabs}
                asNavFor={slider2}
                ref={(slider: Slider) => (setSlider1(slider))}
                className={`slider-thumbnails ${videoCount > 0 && !hasScrolled ? 'show-first-video' : ''}`}
              >
                {allMedia.map((media, index) => {
                  if (media?.publicId) {
                    return (
                      <div
                        className="thumb-slider cloud-thumb-slider"
                        key={`cloud-thumb-${media.publicId}`}
                      >
                        <div className="ar-box-square">
                          <div className="ar-box-in">
                            <AdvancedImage
                              // @ts-ignore
                              cldImg={media.thumb}
                              onError={(e) => {
                                setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                              }}
                              className={`${media.mediaType}-thumb thumb`}
                              plugins={[lazyload()]}
                            />
                            {media.mediaType === 'video' ? (<i className="fa fa-video" />) : null}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      className={`thumb-slider external-thumb-slider${index === activeSlide ? ' current-slide' : ''}`}
                      key={`external-thumb-${media.id}`}
                    >
                      <div className="ar-box-square">
                        <div className="ar-box-in">
                          <div
                            className="video-thumb thumb"
                            style={{ backgroundImage: `url('${media.thumb}')` }}
                          >
                            <i className="fa fa-video" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Slider>

            </div>
          </div>
        )}
        <div className="slider-container-2 col-12 col-lg-10">

          {/* MAIN SLIDER */}
          <Slider
            {...settings}
            asNavFor={slider1}
            ref={(slider: Slider) => (setSlider2(slider))}
            beforeChange={handleSlideChange}
          >
            {allMedia.map((media, index) => {
              if (media?.publicId) {
                return (
                  <div
                    className={`listing-main-slider${index === activeSlide ? ' current-slide' : ''}`}
                    key={`cloud-medium-${media.publicId}`}
                  >
                    <div className="ar-box-square">
                      <div className="ar-box-in">
                        {media.mediaType === 'video' && (
                          // @ts-ignore
                          <AdvancedVideo cldVid={media.embedUrl} controls className="media-medium" />)}
                        {media.mediaType === 'image' && (
                          <AdvancedImage
                            plugins={[lazyload()]}
                            // @ts-ignore
                            cldImg={media.embedUrl}
                            className="media-medium"
                            alt={listingAltTitleAttr()}
                            onError={(e) => {
                              setFallbackImage(e, PUPPY_DEFAULT_IMAGE);
                            }}
                          />
                        )}
                        {listingBoosted && (
                          <BoostBadge />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  className={`listing-main-slider${index === activeSlide ? ' current-slide' : ''}`}
                  key={`external-medium-${media.id}`}
                >
                  <div className="ar-box-square">
                    <div className="ar-box-in">
                      {/*  Youtube Embed Video  */}
                      <iframe
                        title="Video Player"
                        src={media.embedUrl}
                      />
                      {listingBoosted && (
                        <BoostBadge />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>

        </div>
      </div>
    </>
  );
};

if (document.getElementsByClassName('react-listing-slider').length > 0) {
  document.querySelectorAll('.react-listing-slider')
    .forEach((element) => {
      const images = JSON.parse(element.getAttribute('data-images') ?? '');
      const externalMedia = JSON.parse(element.getAttribute('data-external-media') ?? '');
      const listingType = element.getAttribute('data-listing-type');
      const listingName = element.getAttribute('data-listing-name');
      const listingBreedName = element.getAttribute('data-listing-breed-name');
      const listingBoosted = element.getAttribute('data-listing-boosted') === 'true';
      ReactDOM.render(
        <ListingSlider
          images={images}
          externalMedia={externalMedia}
          listingType={listingType}
          listingName={listingName}
          listingBreedName={listingBreedName}
          listingBoosted={listingBoosted}
        />, element,
      );
    });
}

export default ListingSlider;
